import { Component, type PropsWithChildren, type ReactNode } from "react";

interface Props extends PropsWithChildren {
  /** Rendered in place of the children once an error has been caught. */
  fallback: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Contains render errors from a lazily loaded chunk so they cost a panel rather
 * than the whole app. Every deploy replaces the hashed files in /assets, and
 * `lazyWithReload` rethrows once its retry and reload are exhausted — without a
 * boundary above it that reaches the root and unmounts everything into a blank
 * page.
 *
 * Route chunks are covered separately by the router's `defaultErrorComponent`. Reporting happens
 * once, in the root's error hooks (see `reportReactError`), so this boundary only renders.
 */
export default class AppErrorBoundary extends Component<Readonly<Props>, State> {
  public override state: State = { hasError: false };

  public static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  public override render(): ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}
